import { useState } from 'react';
import {
  LayoutDashboard, Calendar, Users, FileText, Settings, Bell, Search, Plus,
  ChevronLeft, ChevronRight, Stethoscope, Clock, MessageCircle, DollarSign,
  TrendingUp, TrendingDown, MoreHorizontal, Video, MapPin, RotateCcw,
  CheckCircle2, AlertCircle
} from 'lucide-react';

// =================================================================
// RADIX COLORS — official scales (light mode)
// Reference: https://www.radix-ui.com/colors
// Each scale has 12 steps. Usage convention:
//   1-2: app/subtle backgrounds   3-5: UI element bg    6-8: borders
//   9-10: solid backgrounds       11: low-contrast text 12: high-contrast text
// =================================================================

const SCALES = {
  slate: ['#FCFCFD', '#F9F9FB', '#EFF0F3', '#E7E8EC', '#DFE0E4', '#D0D3D9', '#C3C6CD', '#B0B4BB', '#8B8D98', '#7E818A', '#60646C', '#1C2024'],
  sand:  ['#FDFDFC', '#F9F9F8', '#F1F0EF', '#E9E8E6', '#E2E1DE', '#DAD9D6', '#C8C7C1', '#B1B0AB', '#90918E', '#84847F', '#63635E', '#21201C'],
  mauve: ['#FDFCFD', '#FAF9FB', '#F2EFF3', '#EAE7EC', '#E1DEE6', '#D7D1DB', '#C8C2CF', '#B3ABBD', '#95909E', '#85808D', '#65636D', '#211F26'],

  iris:   ['#FDFDFF', '#F8F8FF', '#F0F4FF', '#E6EDFE', '#D9E2FC', '#C6D4F9', '#AEC0F5', '#8DA4EF', '#5B5BD6', '#5353CE', '#4747C2', '#1F1F7A'],
  indigo: ['#FDFDFE', '#F7F9FF', '#EDF2FE', '#E1E9FF', '#D2DEFF', '#C1D0FF', '#ABBDF9', '#8DA4EF', '#3E63DD', '#3A5CCC', '#3451B2', '#1F2D5C'],
  jade:   ['#FBFEFD', '#EFFDF6', '#E9F9EE', '#DDF3E4', '#CCEBD7', '#B4DFC4', '#8ECEAA', '#56BA9F', '#29A383', '#26946A', '#208368', '#1D3B31'],
  amber:  ['#FEFDFB', '#FEFBE9', '#FFF7C2', '#FFEE9C', '#FBE577', '#F3D673', '#E9C368', '#E2A336', '#FFB224', '#FFA01C', '#AD5700', '#4E2009'],

  grass:  ['#FBFEFB', '#F3FCF3', '#EBF9EB', '#DEF3DF', '#CEEBCF', '#B7DFBA', '#97CF9C', '#65BA75', '#46A758', '#3E9B4F', '#2A7E3B', '#203C25'],
  tomato: ['#FFFCFC', '#FFF8F7', '#FEEBE7', '#FFDBD1', '#FFCDBE', '#FDBDAA', '#F5A390', '#EC8E7B', '#E54D2E', '#DC3E1C', '#CA3214', '#341711'],
};

// =================================================================
// PALETTES — 4 curated combinations (gray-scale + accent + semantic)
// =================================================================

const makePalette = (cfg) => {
  const g = SCALES[cfg.gray];
  const a = SCALES[cfg.accent];
  const s = SCALES[cfg.success || 'jade'];
  const d = SCALES[cfg.danger || 'tomato'];
  const w = SCALES[cfg.warn || 'amber'];
  return {
    name: cfg.name,
    desc: cfg.desc,
    tag: cfg.tag,
    font: cfg.font,
    gray: g,
    accent: a,
    success: s,
    danger: d,
    warn: w,
    // readable bindings
    bg: g[0],
    surface: '#FFFFFF',
    subtle: g[1],
    border: g[3],
    borderStrong: g[5],
    inkLow: g[8],
    inkMuted: g[10],
    ink: g[11],
  };
};

const PALETTES = {
  A: makePalette({
    name: 'Slate · Iris',
    desc: 'Paleta neutra + roxo-lavanda da Radix. Ultra profissional, estilo SaaS moderno (tipo Linear/WorkOS).',
    tag: 'SaaS',
    font: 'font-geist',
    gray: 'slate',
    accent: 'iris',
    success: 'jade',
    danger: 'tomato',
    warn: 'amber',
  }),
  B: makePalette({
    name: 'Sand · Amber',
    desc: 'Cinza quente (bege) + âmbar discreto. Visual editorial, acolhedor, aspecto clínica premium.',
    tag: 'Editorial',
    font: 'font-manrope',
    gray: 'sand',
    accent: 'amber',
    success: 'jade',
    danger: 'tomato',
    warn: 'amber',
  }),
  C: makePalette({
    name: 'Mauve · Jade',
    desc: 'Cinza levemente roxo + verde-jade calmo. Vibe médica/wellness discreta, zero drama.',
    tag: 'Wellness',
    font: 'font-geist',
    gray: 'mauve',
    accent: 'jade',
    success: 'jade',
    danger: 'tomato',
    warn: 'amber',
  }),
  D: makePalette({
    name: 'Slate · Indigo',
    desc: 'Cinza neutro + azul-royal clássico. Formal, confiável, profissional médico tradicional.',
    tag: 'Clássico',
    font: 'font-geist',
    gray: 'slate',
    accent: 'indigo',
    success: 'grass',
    danger: 'tomato',
    warn: 'amber',
  }),
};

const FONT_LINK = (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&family=Manrope:wght@400;500;600;700&display=swap');
    .font-geist { font-family: 'Geist', system-ui, -apple-system, sans-serif; }
    .font-manrope { font-family: 'Manrope', system-ui, -apple-system, sans-serif; }
    .font-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
  `}</style>
);

// ======================= Atoms =======================
const Avatar = ({ name, size = 'md', p }) => {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  const sizes = { sm: 'w-7 h-7 text-[10px]', md: 'w-9 h-9 text-xs', lg: 'w-11 h-11 text-sm' };
  return (
    <div
      className={`${sizes[size]} rounded-full flex items-center justify-center font-semibold flex-shrink-0`}
      style={{ background: p.gray[3], color: p.gray[11] }}
    >
      {initials}
    </div>
  );
};

const StatusPill = ({ status, p }) => {
  const map = {
    confirmada: { bg: p.success[2], fg: p.success[11], dot: p.success[9], label: 'Confirmada' },
    agendada:   { bg: p.warn[2],    fg: p.warn[11],    dot: p.warn[9],    label: 'Não confirmada' },
    realizada:  { bg: p.gray[2],    fg: p.gray[11],    dot: p.gray[9],    label: 'Realizada' },
    cancelada:  { bg: p.danger[2],  fg: p.danger[11],  dot: p.danger[9],  label: 'Cancelada' },
    falta:      { bg: p.warn[3],    fg: p.warn[11],    dot: p.warn[10],   label: 'Falta' },
  }[status];
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide"
      style={{ background: map.bg, color: map.fg }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: map.dot }} />
      {map.label}
    </span>
  );
};

const TipoTag = ({ tipo, p }) => {
  const map = {
    presencial: { icon: MapPin,   label: 'Presencial', color: p.accent[11], bg: p.accent[3] },
    online:     { icon: Video,    label: 'Online',     color: p.ink,        bg: p.gray[3]   },
    retorno:    { icon: RotateCcw,label: 'Retorno',    color: p.warn[11],   bg: p.warn[3]   },
  }[tipo];
  const Icon = map.icon;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
      style={{ background: map.bg, color: map.color }}>
      <Icon className="w-2.5 h-2.5" strokeWidth={2.2} />
      {map.label}
    </span>
  );
};

const PagamentoBadge = ({ estado, valor, p }) => {
  const cfg = {
    zero:    { bg: p.danger[3],  fg: p.danger[11]  },
    parcial: { bg: p.warn[3],    fg: p.warn[11]    },
    pago:    { bg: p.success[3], fg: p.success[11] },
  }[estado];
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-mono font-semibold tabular-nums"
      style={{ background: cfg.bg, color: cfg.fg }}>
      R$ {valor}
    </span>
  );
};

// ======================= Chrome =======================
const Sidebar = ({ active, p }) => {
  const items = [
    { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { key: 'agenda', icon: Calendar, label: 'Agenda' },
    { key: 'pacientes', icon: Users, label: 'Pacientes' },
    { key: 'prontuarios', icon: FileText, label: 'Prontuários' },
    { key: 'configuracoes', icon: Settings, label: 'Configurações' },
  ];
  return (
    <aside className="w-60 flex flex-col flex-shrink-0 border-r" style={{ background: p.surface, borderColor: p.border }}>
      <div className="p-5 border-b flex items-center gap-2.5" style={{ borderColor: p.border }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: p.ink }}>
          <Stethoscope className="w-4 h-4" strokeWidth={2} style={{ color: p.gray[0] }} />
        </div>
        <div>
          <div className="text-[14px] font-semibold leading-none tracking-tight" style={{ color: p.ink }}>Agenda Mais</div>
          <div className="text-[9px] tracking-[0.18em] uppercase mt-1" style={{ color: p.inkLow }}>Med</div>
        </div>
      </div>
      <nav className="flex-1 p-2 space-y-0.5">
        {items.map(it => {
          const Icon = it.icon;
          const isActive = it.key === active;
          return (
            <a key={it.key} href="#"
              className="flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium transition-colors"
              style={isActive
                ? { background: p.accent[3], color: p.accent[11] }
                : { color: p.ink }}
            >
              <Icon className="w-4 h-4" strokeWidth={1.8} style={{ color: isActive ? p.accent[11] : p.inkMuted }} />
              {it.label}
            </a>
          );
        })}
      </nav>
      <div className="p-2 border-t" style={{ borderColor: p.border }}>
        <div className="flex items-center gap-2 p-1.5">
          <Avatar name="Nathalie Sanches" size="sm" p={p} />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-semibold truncate" style={{ color: p.ink }}>Dra. Nathalie</div>
            <div className="text-[10px] truncate" style={{ color: p.inkLow }}>Ginecologia</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

const TopBar = ({ title, subtitle, p }) => (
  <header className="border-b px-7 py-4 flex items-center justify-between flex-shrink-0" style={{ background: p.surface, borderColor: p.border }}>
    <div>
      <h1 className="text-xl font-semibold tracking-tight" style={{ color: p.ink }}>{title}</h1>
      {subtitle && <p className="text-[12px] mt-0.5" style={{ color: p.inkLow }}>{subtitle}</p>}
    </div>
    <div className="flex items-center gap-2">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.8} style={{ color: p.inkLow }} />
        <input type="text" placeholder="Buscar paciente, consulta…"
          className="pl-9 pr-3 py-1.5 text-[12px] rounded-md w-64 focus:outline-none placeholder:opacity-60"
          style={{ background: p.subtle, border: `1px solid ${p.border}`, color: p.ink }}/>
      </div>
      <button className="w-8 h-8 rounded-md flex items-center justify-center transition-colors relative hover:bg-black/[0.03]" style={{ color: p.inkMuted }}>
        <Bell className="w-4 h-4" strokeWidth={1.8} />
        <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full" style={{ background: p.accent[9] }}></span>
      </button>
    </div>
  </header>
);

// ======================= AGENDA (desktop) =======================
const AgendaScreen = ({ p }) => {
  const consultas = [
    { hora: '09:00', dur: '50min', nome: 'Beatriz Mendes Oliveira', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: '320', pg: 'pago' },
    { hora: '10:30', dur: '40min', nome: 'Carla Souza Lima', tipo: 'online', status: 'agendada', proc: 'Reposição Hormonal', valor: '0', pg: 'zero' },
    { hora: '14:00', dur: '30min', nome: 'Helena Martins Barbosa', tipo: 'retorno', status: 'confirmada', proc: 'Retorno', valor: null },
    { hora: '15:30', dur: '60min', nome: 'Mariana Dias Cardoso', tipo: 'presencial', status: 'realizada', proc: 'Preventivo · Papanicolau', valor: '250', pg: 'pago' },
    { hora: '17:00', dur: '40min', nome: 'Paula Ribeiro Santos', tipo: 'presencial', status: 'agendada', proc: 'Consulta Ginecológica', valor: '150', pg: 'parcial' },
  ];

  return (
    <div className={`flex h-full ${p.font}`} style={{ background: p.bg }}>
      <Sidebar active="agenda" p={p} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar title="Agenda" subtitle="Quinta · 30 de abril, 2026" p={p} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <button className="px-3 py-1.5 text-[12px] font-medium rounded-md border"
                style={{ borderColor: p.border, color: p.ink, background: p.surface }}>Hoje</button>
              <div className="flex items-center rounded-md border overflow-hidden" style={{ borderColor: p.border, background: p.surface }}>
                <button className="p-1.5 hover:bg-black/[0.03]" style={{ color: p.inkMuted }}><ChevronLeft className="w-4 h-4" strokeWidth={1.8}/></button>
                <div className="w-px h-5" style={{ background: p.border }}/>
                <button className="p-1.5 hover:bg-black/[0.03]" style={{ color: p.inkMuted }}><ChevronRight className="w-4 h-4" strokeWidth={1.8}/></button>
              </div>
              <h2 className="text-[14px] font-semibold ml-2 tracking-tight" style={{ color: p.ink }}>30 abril → 02 maio 2026</h2>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex p-0.5 rounded-md" style={{ background: p.gray[2], border: `1px solid ${p.border}` }}>
                <button className="px-3 py-1 text-[11px] font-medium rounded" style={{ background: p.surface, color: p.ink, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Dia</button>
                <button className="px-3 py-1 text-[11px] font-medium rounded" style={{ color: p.inkMuted }}>Semana</button>
                <button className="px-3 py-1 text-[11px] font-medium rounded" style={{ color: p.inkMuted }}>Mês</button>
              </div>
              <button className="px-3 py-1.5 rounded-md text-[12px] font-semibold flex items-center gap-1.5"
                style={{ background: p.accent[9], color: '#FFFFFF' }}>
                <Plus className="w-3.5 h-3.5" strokeWidth={2}/>
                Nova Consulta
              </button>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-3 mb-5">
            {[
              { l: 'Hoje', v: '5', s: '2 confirmadas', accent: false },
              { l: 'Previsto', v: 'R$ 720', s: 'em recebíveis', accent: false },
              { l: 'Recebido', v: 'R$ 570', s: '79% do total', accent: true },
              { l: 'Pendente', v: 'R$ 150', s: '1 consulta', accent: false },
            ].map((x, i) => (
              <div key={i} className="rounded-lg border p-3.5" style={{ background: p.surface, borderColor: p.border }}>
                <div className="text-[10px] uppercase tracking-[0.12em] font-semibold mb-1.5" style={{ color: p.inkLow }}>{x.l}</div>
                <div className="font-mono text-[22px] font-semibold tabular-nums leading-none" style={{ color: x.accent ? p.success[11] : p.ink }}>{x.v}</div>
                <div className="text-[11px] mt-1.5" style={{ color: p.inkLow }}>{x.s}</div>
              </div>
            ))}
          </div>

          {/* List card */}
          <div className="rounded-lg border overflow-hidden" style={{ background: p.surface, borderColor: p.border }}>
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: p.border, background: p.subtle }}>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: p.inkLow }}>Quinta · 30 abril</span>
                <span className="w-1 h-1 rounded-full" style={{ background: p.inkLow }}/>
                <span className="text-[11px]" style={{ color: p.inkLow }}>5 consultas</span>
              </div>
              <button className="text-[11px] font-medium hover:underline" style={{ color: p.accent[11] }}>Ver detalhes →</button>
            </div>

            <div>
              {consultas.map((c, i) => (
                <div key={i} className="flex items-stretch border-b last:border-b-0 transition-colors hover:bg-black/[0.015] cursor-pointer"
                  style={{ borderColor: p.gray[2] }}>
                  {/* Time */}
                  <div className="px-5 py-3.5 flex flex-col justify-center border-r" style={{ borderColor: p.gray[2], width: 110 }}>
                    <div className="font-mono text-[14px] font-semibold tabular-nums" style={{ color: p.ink }}>{c.hora}</div>
                    <div className="font-mono text-[10px] mt-0.5 tabular-nums" style={{ color: p.inkLow }}>{c.dur}</div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 px-5 py-3.5 flex items-center gap-4">
                    <Avatar name={c.nome} p={p} size="md" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[13px] font-semibold truncate" style={{ color: p.ink }}>{c.nome}</span>
                        <TipoTag tipo={c.tipo} p={p} />
                      </div>
                      <div className="text-[11px]" style={{ color: p.inkLow }}>{c.proc}</div>
                    </div>

                    <StatusPill status={c.status} p={p} />

                    <div className="w-20 text-right">
                      {c.valor !== null && (
                        <PagamentoBadge estado={c.pg} valor={c.valor} p={p} />
                      )}
                    </div>

                    <button className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-black/[0.04]" style={{ color: p.inkMuted }}>
                      <MoreHorizontal className="w-4 h-4" strokeWidth={1.8}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-5 py-3 flex items-center justify-between text-[11px]" style={{ color: p.inkLow, background: p.subtle, borderTop: `1px solid ${p.border}` }}>
              <span>5 consultas · R$ 570 recebido · R$ 150 pendente</span>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full" style={{ background: p.success[9] }}/> Pago</span>
                <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full" style={{ background: p.warn[9] }}/> Parcial</span>
                <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full" style={{ background: p.danger[9] }}/> Não pago</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// ======================= DASHBOARD =======================
const DashboardScreen = ({ p }) => (
  <div className={`flex h-full ${p.font}`} style={{ background: p.bg }}>
    <Sidebar active="dashboard" p={p} />
    <div className="flex-1 flex flex-col overflow-hidden">
      <TopBar title="Bom dia, Nathalie." subtitle="Quinta · 30 abril · 8 consultas hoje" p={p} />
      <main className="flex-1 overflow-y-auto p-6 space-y-5">
        <div className="grid grid-cols-4 gap-3">
          {[
            { l: 'Consultas hoje', v: '8', d: '+2 vs média', up: true },
            { l: 'Pacientes ativos', v: '342', d: '+12 este mês', up: true },
            { l: 'Receita do mês', v: 'R$ 28.450', d: '+12.4%', up: true, accent: true },
            { l: 'No-show rate', v: '4.2%', d: '-1.1% vs mês passado', up: false },
          ].map((m, i) => (
            <div key={i} className="rounded-lg border p-5" style={{ background: p.surface, borderColor: p.border }}>
              <div className="text-[10px] uppercase tracking-[0.12em] font-semibold mb-3" style={{ color: p.inkLow }}>{m.l}</div>
              <div className="font-mono text-[28px] font-semibold tabular-nums tracking-tight leading-none"
                style={{ color: m.accent ? p.accent[11] : p.ink }}>{m.v}</div>
              <div className="flex items-center gap-1 mt-3 text-[11px]" style={{ color: m.up ? p.success[11] : p.inkLow }}>
                {m.up ? <TrendingUp className="w-3 h-3" strokeWidth={2}/> : <TrendingDown className="w-3 h-3" strokeWidth={2}/>}
                {m.d}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 rounded-lg border overflow-hidden" style={{ background: p.surface, borderColor: p.border }}>
            <div className="px-5 py-3.5 border-b flex items-center justify-between" style={{ borderColor: p.border }}>
              <div>
                <div className="text-[13px] font-semibold tracking-tight" style={{ color: p.ink }}>Próximas consultas</div>
                <div className="text-[11px] mt-0.5" style={{ color: p.inkLow }}>5 confirmadas · 3 pendentes</div>
              </div>
              <button className="text-[11px] font-medium hover:underline" style={{ color: p.accent[11] }}>Ver agenda →</button>
            </div>
            <div>
              {[
                { hora: '09:00', nome: 'Beatriz Mendes Oliveira', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: '320', pg: 'pago' },
                { hora: '10:30', nome: 'Carla Souza Lima', tipo: 'online', status: 'agendada', proc: 'Reposição Hormonal', valor: '0', pg: 'zero' },
                { hora: '14:00', nome: 'Helena Martins Barbosa', tipo: 'retorno', status: 'confirmada', proc: 'Retorno · Consulta', valor: null },
                { hora: '15:30', nome: 'Mariana Dias Cardoso', tipo: 'presencial', status: 'confirmada', proc: 'Preventivo', valor: '250', pg: 'pago' },
              ].map((c, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0"
                  style={{ borderColor: p.gray[2] }}>
                  <div className="font-mono text-[12px] tabular-nums w-12" style={{ color: p.ink }}>{c.hora}</div>
                  <Avatar name={c.nome} p={p} size="md"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold truncate" style={{ color: p.ink }}>{c.nome}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <TipoTag tipo={c.tipo} p={p} />
                      <span className="text-[11px]" style={{ color: p.inkLow }}>· {c.proc}</span>
                    </div>
                  </div>
                  <StatusPill status={c.status} p={p} />
                  <div className="w-16 text-right">
                    {c.valor !== null && <PagamentoBadge estado={c.pg} valor={c.valor} p={p}/>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border p-5 flex flex-col" style={{ background: p.surface, borderColor: p.border }}>
            <div className="text-[13px] font-semibold tracking-tight mb-1" style={{ color: p.ink }}>Retornos a agendar</div>
            <div className="text-[11px] mb-4" style={{ color: p.inkLow }}>Pacientes com retorno pendente</div>
            {[
              { nome: 'Aline Costa', dias: '12 dias', tipo: 'warn' },
              { nome: 'Sandra Silva', dias: '7 dias', tipo: 'ok' },
              { nome: 'Júlia Barbosa', dias: '45 dias', tipo: 'late' },
            ].map((r, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5 border-b last:border-b-0" style={{ borderColor: p.gray[2] }}>
                <Avatar name={r.nome} p={p} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-semibold truncate" style={{ color: p.ink }}>{r.nome}</div>
                  <div className="text-[10px]" style={{ color: p.inkLow }}>{r.dias} desde última</div>
                </div>
                {r.tipo === 'late' && <AlertCircle className="w-3.5 h-3.5" strokeWidth={2} style={{ color: p.danger[11] }}/>}
                {r.tipo === 'warn' && <Clock className="w-3.5 h-3.5" strokeWidth={2} style={{ color: p.warn[11] }}/>}
                {r.tipo === 'ok'   && <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} style={{ color: p.success[11] }}/>}
              </div>
            ))}
            <button className="mt-4 w-full text-[12px] font-medium py-2 rounded-md border"
              style={{ borderColor: p.border, color: p.ink, background: p.subtle }}>Ver controle de retornos</button>
          </div>
        </div>
      </main>
    </div>
  </div>
);

// ======================= MOBILE =======================
const MobileScreen = ({ p }) => (
  <div className={`${p.font} h-full overflow-y-auto`} style={{ background: p.bg }}>
    <header className="sticky top-0 border-b px-5 py-4 z-10" style={{ background: p.surface, borderColor: p.border }}>
      <div className="flex items-center justify-between mb-3.5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: p.inkLow }}>QUI · 30 ABRIL</div>
          <h1 className="text-xl font-semibold tracking-tight mt-0.5" style={{ color: p.ink }}>Agenda</h1>
        </div>
        <div className="flex items-center gap-2">
          <button className="w-9 h-9 rounded-full flex items-center justify-center border" style={{ background: p.surface, borderColor: p.border, color: p.ink }}>
            <Search className="w-4 h-4" strokeWidth={1.8}/>
          </button>
          <button className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: p.accent[9], color: '#FFF' }}>
            <Plus className="w-4 h-4" strokeWidth={2}/>
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between gap-1">
        {['28', '29', '30', '01', '02'].map((d, i) => {
          const isToday = i === 2;
          const dayLabel = ['SEG', 'TER', 'QUI', 'SEX', 'SÁB'][i];
          return (
            <button key={i} className="flex-1 flex flex-col items-center gap-0.5 py-2 rounded-lg"
              style={isToday ? { background: p.accent[9], color: '#FFF' } : { color: p.inkLow }}>
              <span className="text-[9px] uppercase tracking-[0.12em] font-bold">{dayLabel}</span>
              <span className="font-mono text-[15px] font-semibold tabular-nums" style={{ color: isToday ? '#FFF' : p.ink }}>{d}</span>
            </button>
          );
        })}
      </div>
    </header>

    <div className="p-4 space-y-2.5 pb-24">
      {[
        { hora: '09:00', nome: 'Beatriz Mendes Oliveira', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: '320', pg: 'pago' },
        { hora: '10:30', nome: 'Carla Souza Lima', tipo: 'online', status: 'agendada', proc: 'Reposição Hormonal', valor: '0', pg: 'zero' },
        { hora: '14:00', nome: 'Helena Martins Barbosa', tipo: 'retorno', status: 'confirmada', proc: 'Retorno · Consulta', valor: null },
        { hora: '15:30', nome: 'Mariana Dias Cardoso', tipo: 'presencial', status: 'realizada', proc: 'Preventivo', valor: '250', pg: 'pago' },
      ].map((c, i) => (
        <div key={i} className="rounded-xl border p-3.5" style={{ background: p.surface, borderColor: p.border }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[13px] font-semibold tabular-nums" style={{ color: p.ink }}>{c.hora}</span>
              <span className="w-1 h-1 rounded-full" style={{ background: p.inkLow }}/>
              <TipoTag tipo={c.tipo} p={p} />
            </div>
            <StatusPill status={c.status} p={p} />
          </div>
          <div className="flex items-center gap-3">
            <Avatar name={c.nome} p={p} size="md" />
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold truncate" style={{ color: p.ink }}>{c.nome}</div>
              <div className="text-[11px] truncate" style={{ color: p.inkLow }}>{c.proc}</div>
            </div>
            {c.valor !== null && <PagamentoBadge estado={c.pg} valor={c.valor} p={p}/>}
          </div>
        </div>
      ))}
    </div>

    <nav className="fixed bottom-0 left-0 right-0 border-t px-2 py-2 flex items-center justify-around max-w-[420px] mx-auto"
      style={{ background: p.surface, borderColor: p.border }}>
      {[
        { icon: LayoutDashboard, label: 'Início' },
        { icon: Calendar, label: 'Agenda', active: true },
        { icon: Users, label: 'Pacientes' },
        { icon: MessageCircle, label: 'Mensagens' },
        { icon: Settings, label: 'Mais' },
      ].map((it, i) => {
        const Icon = it.icon;
        return (
          <button key={i} className="flex flex-col items-center gap-0.5 px-3 py-1.5"
            style={{ color: it.active ? p.accent[11] : p.inkLow }}>
            <Icon className="w-4 h-4" strokeWidth={1.8}/>
            <span className="text-[9px] uppercase tracking-[0.1em] font-semibold">{it.label}</span>
          </button>
        );
      })}
    </nav>
  </div>
);

// ======================= CONSULTA DETAIL (new — shows Financial module) =======================
const ConsultaScreen = ({ p }) => (
  <div className={`flex h-full ${p.font}`} style={{ background: p.bg }}>
    <Sidebar active="agenda" p={p} />
    <div className="flex-1 flex flex-col overflow-hidden">
      <TopBar title="Consulta" subtitle="Beatriz Mendes Oliveira · Quinta 30 abr, 09:00" p={p} />
      <main className="flex-1 overflow-y-auto p-6 space-y-5">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 space-y-4">
            {/* Header card */}
            <div className="rounded-lg border p-5 flex items-center gap-5" style={{ background: p.surface, borderColor: p.border }}>
              <Avatar name="Beatriz Mendes Oliveira" p={p} size="lg"/>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-[18px] font-semibold tracking-tight" style={{ color: p.ink }}>Beatriz Mendes Oliveira</h2>
                  <TipoTag tipo="presencial" p={p} />
                </div>
                <div className="text-[12px]" style={{ color: p.inkLow }}>42 anos · (11) 98765-4321 · beatriz@email.com</div>
              </div>
              <StatusPill status="confirmada" p={p} />
            </div>

            {/* Financial module */}
            <div className="rounded-lg border overflow-hidden" style={{ background: p.surface, borderColor: p.border }}>
              <div className="px-5 py-3.5 border-b flex items-center justify-between" style={{ borderColor: p.border }}>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4" strokeWidth={1.8} style={{ color: p.accent[11] }}/>
                  <div className="text-[13px] font-semibold tracking-tight" style={{ color: p.ink }}>Financeiro</div>
                </div>
                <button className="px-3 py-1.5 text-[11px] font-semibold rounded-md flex items-center gap-1.5"
                  style={{ background: p.accent[9], color: '#FFF' }}>
                  <Plus className="w-3 h-3" strokeWidth={2.2}/>
                  Novo Lançamento
                </button>
              </div>

              <div className="grid grid-cols-3 divide-x" style={{ borderColor: p.border }}>
                {[
                  { l: 'Valor total', v: 'R$ 320,00', c: p.ink },
                  { l: 'Recebido', v: 'R$ 320,00', c: p.success[11] },
                  { l: 'Restante', v: 'R$ 0,00', c: p.inkLow },
                ].map((x, i) => (
                  <div key={i} className="p-5" style={{ borderColor: p.border }}>
                    <div className="text-[10px] uppercase tracking-[0.12em] font-semibold mb-2" style={{ color: p.inkLow }}>{x.l}</div>
                    <div className="font-mono text-[20px] font-semibold tabular-nums" style={{ color: x.c }}>{x.v}</div>
                  </div>
                ))}
              </div>

              <div className="px-5 pb-5">
                <div className="h-2 rounded-full overflow-hidden mt-1" style={{ background: p.gray[3] }}>
                  <div className="h-full rounded-full" style={{ width: '100%', background: p.success[9] }}/>
                </div>
                <div className="flex items-center justify-between mt-2 text-[11px]">
                  <span style={{ color: p.inkLow }}>100% quitado</span>
                  <span className="font-mono tabular-nums font-semibold" style={{ color: p.success[11] }}>✓ Pago</span>
                </div>
              </div>

              <div className="border-t" style={{ borderColor: p.border }}>
                <div className="px-5 py-2.5 text-[10px] uppercase tracking-[0.12em] font-semibold" style={{ color: p.inkLow, background: p.subtle }}>
                  Lançamentos
                </div>
                {[
                  { data: '30/04/2026', forma: 'PIX', valor: '200,00', obs: 'Sinal de pagamento' },
                  { data: '30/04/2026', forma: 'Cartão', valor: '120,00', obs: 'Quitação' },
                ].map((l, i) => (
                  <div key={i} className="px-5 py-3 flex items-center gap-4 border-t" style={{ borderColor: p.gray[2] }}>
                    <div className="font-mono text-[11px] tabular-nums" style={{ color: p.inkLow }}>{l.data}</div>
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: p.accent[3], color: p.accent[11] }}>{l.forma}</span>
                    <span className="text-[12px] flex-1" style={{ color: p.ink }}>{l.obs}</span>
                    <span className="font-mono text-[13px] tabular-nums font-semibold" style={{ color: p.ink }}>R$ {l.valor}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Side column */}
          <div className="space-y-4">
            <div className="rounded-lg border p-5" style={{ background: p.surface, borderColor: p.border }}>
              <div className="text-[13px] font-semibold mb-3 tracking-tight" style={{ color: p.ink }}>Ações</div>
              <div className="space-y-2">
                <button className="w-full py-2 text-[12px] font-semibold rounded-md" style={{ background: p.success[9], color: '#FFF' }}>Marcar como Realizada</button>
                <button className="w-full py-2 text-[12px] font-semibold rounded-md border" style={{ background: p.surface, borderColor: p.border, color: p.ink }}>Editar consulta</button>
                <button className="w-full py-2 text-[12px] font-semibold rounded-md border" style={{ background: p.surface, borderColor: p.danger[6], color: p.danger[11] }}>Cancelar</button>
              </div>
            </div>
            <div className="rounded-lg border p-5" style={{ background: p.surface, borderColor: p.border }}>
              <div className="text-[13px] font-semibold mb-3 tracking-tight" style={{ color: p.ink }}>WhatsApp</div>
              <div className="flex items-center gap-2 text-[11px]" style={{ color: p.success[11] }}>
                <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2}/>
                Confirmação enviada há 2h
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
);

// ======================= PAGE =======================
export default function AppMockupsRadixPage() {
  const [paletteKey, setPaletteKey] = useState('A');
  const [screen, setScreen] = useState('agenda');
  const p = PALETTES[paletteKey];
  const isMobile = screen === 'mobile';

  const Screen = {
    agenda: AgendaScreen,
    dashboard: DashboardScreen,
    consulta: ConsultaScreen,
    mobile: MobileScreen,
  }[screen];

  return (
    <>
      {FONT_LINK}
      <div className="min-h-screen p-6" style={{ background: '#F4F5F7' }}>
        <div className="max-w-[1500px] mx-auto">
          <header className="mb-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#6B7280' }}>V3 · Radix Colors</span>
              <span className="w-1 h-1 rounded-full" style={{ background: '#9CA3AF' }}/>
              <span className="text-[11px]" style={{ color: '#6B7280' }}>Shadcn/UI + @radix-ui/colors</span>
            </div>
            <h1 className="font-geist text-3xl font-semibold tracking-tight" style={{ color: '#0F172A' }}>Redesign com Radix Colors</h1>
            <p className="text-sm mt-1" style={{ color: '#64748B' }}>Paleta oficial Radix · 12 tons por cor · acessibilidade AAA garantida · escolha 1 dos 4 temas</p>
          </header>

          {/* Palette tabs */}
          <div className="grid grid-cols-4 gap-2 mb-3">
            {Object.entries(PALETTES).map(([k, pal]) => {
              const active = k === paletteKey;
              return (
                <button key={k} onClick={() => setPaletteKey(k)}
                  className="p-3.5 text-left rounded-lg border transition-all"
                  style={{
                    background: active ? '#FFFFFF' : 'rgba(255,255,255,0.7)',
                    borderColor: active ? pal.accent[9] : '#E2E8F0',
                    boxShadow: active ? `0 0 0 2px ${pal.accent[9]}22, 0 2px 8px rgba(0,0,0,0.04)` : 'none'
                  }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[9px] uppercase tracking-[0.15em] font-bold" style={{ color: '#94A3B8' }}>Tema {k} · {pal.tag}</span>
                    <div className="flex gap-0.5">
                      {[1, 3, 6, 9, 11].map(s => <span key={s} className="w-3 h-3 rounded-full" style={{ background: pal.gray[s] }}/>)}
                      <span className="w-3 h-3 rounded-full ml-1" style={{ background: pal.accent[9] }}/>
                    </div>
                  </div>
                  <div className="font-semibold text-[14px]" style={{ color: '#0F172A' }}>{pal.name}</div>
                  <div className="text-[11px] mt-0.5 leading-snug" style={{ color: '#64748B' }}>{pal.desc}</div>
                </button>
              );
            })}
          </div>

          {/* Screen tabs */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {[
              { k: 'agenda', label: 'Agenda' },
              { k: 'dashboard', label: 'Dashboard' },
              { k: 'consulta', label: 'Detalhe Consulta · Financeiro' },
              { k: 'mobile', label: 'Mobile (iPhone)' },
            ].map(s => (
              <button key={s.k} onClick={() => setScreen(s.k)}
                className="px-3 py-1.5 text-[12px] font-medium rounded-md transition-all border"
                style={screen === s.k
                  ? { background: p.ink, color: p.gray[0], borderColor: p.ink }
                  : { background: '#FFF', color: '#475569', borderColor: '#E2E8F0' }}>
                {s.label}
              </button>
            ))}
          </div>

          {/* Frame */}
          <div className="rounded-2xl overflow-hidden shadow-2xl"
            style={{
              border: '8px solid #0F172A',
              background: isMobile ? '#000' : '#F1F5F9',
              width: isMobile ? 420 : '100%',
              height: 740,
              margin: isMobile ? '0 auto' : undefined,
            }}>
            <Screen p={p} />
          </div>

          <p className="text-xs mt-4 text-center" style={{ color: '#94A3B8' }}>
            Todos os componentes usam Shadcn/UI (Radix Primitives) · cores são tokens oficiais Radix Colors · 100% das funcionalidades atuais mantidas.
          </p>
        </div>
      </div>
    </>
  );
}
